import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import BadgeRail from "../components/BadgeRail";
import CertificateNameModal from "../components/CertificateNameModal";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import { toast } from "sonner";
import { Award, ExternalLink, Lock, CheckCircle2, ClipboardCheck, Sparkles, Linkedin, Copy } from "lucide-react";
import { Link } from "react-router-dom";

export const CERTIFICATE_IMAGE =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_cpg-institute-ux/artifacts/fl7tvv0i_001%20certificate.png";

export default function Progress() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [modules, setModules] = useState([]);
  const [issued, setIssued] = useState([]);
  const [progress, setProgress] = useState(null);
  const [master, setMaster] = useState(null);
  const [claimingMaster, setClaimingMaster] = useState(false);
  // Display-name capture — one-time modal for every founder before
  // their first certificate download. `pendingDownloadRef` remembers
  // which cert the user tried to download so we can resume the flow
  // as soon as the name save completes.
  const [displayName, setDisplayName] = useState(null); // string | null
  const [nameLocked, setNameLocked] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null); // { kind: 'cert'|'master', slug?, credential_id? }

  const load = () => {
    api.get("/certifications").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/certifications/my").then((r) => setIssued(r.data)).catch(() => {});
    api.get("/progress/me").then((r) => setProgress(r.data)).catch(() => setProgress(null));
    api.get("/certificates/master/eligibility").then((r) => setMaster(r.data)).catch(() => setMaster(null));
    api.get("/certifications/display-name").then((r) => {
      setDisplayName(r.data?.display_name || null);
      setNameLocked(!!r.data?.locked);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openPdf = (credentialId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/certifications/pdf/${credentialId}`;
    window.open(url, "_blank");
  };

  // Guard every download click. If the display name has not been
  // captured yet, stash the download intent and pop the confirm modal
  // instead of hitting the PDF endpoint (which would 409 anyway).
  const ensureNameThen = (fn) => {
    if (nameLocked) { fn(); return; }
    setPendingDownload({ run: fn });
    setShowNameModal(true);
  };

  const issuedMap = Object.fromEntries(issued.map((c) => [c.cert_slug, c]));
  const claim = async (slug) => {
    ensureNameThen(async () => {
      try {
        const { data } = await api.post("/certifications/issue", { cert_slug: slug });
        openPdf(data.credential_id);
        load();
      } catch { /* toast handled elsewhere */ }
    });
  };
  const claimMaster = async () => {
    ensureNameThen(async () => {
      setClaimingMaster(true);
      try {
        const { data } = await api.post("/certificates/master/claim");
        toast.success("CPG Master credential issued");
        // Open the printable PDF using the same route the primary uses; if it
        // 404s the toast still confirms the claim so the user can find it in
        // "My credentials".
        if (data?.credential_id) openPdf(data.credential_id);
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't claim CPG Master yet");
      } finally {
        setClaimingMaster(false);
      }
    });
  };

  const bySlug = Object.fromEntries(modules.map((m) => [m.slug, m]));
  const primary = certs.find((c) => c.tier === "primary");
  const specialists = certs.filter((c) => c.tier === "specialist");
  const coreModules = modules.filter((m) => m.track === "core");
  const bonusModules = modules.filter((m) => m.track === "bonus");
  const passedSlugs = new Set(progress?.passed_slugs || []);
  const completedSlugs = new Set(progress?.completed_slugs || []);
  const coreDone = progress?.core_completed ?? 0;
  const coreTotal = progress?.core_total ?? coreModules.length;
  const primaryEarned = coreTotal > 0 && coreDone >= coreTotal;
  const primaryPct = coreTotal ? Math.round((coreDone / coreTotal) * 100) : 0;

  return (
    <AppShell title="Progress & Certificates">
      <div className="w-full">
        <PageHero
          eyebrow="Certifications"
          icon={Award}
          title="Your certification path"
          subtitle={<>Earn the <strong>Certified CPG Founder™</strong> credential and up to six specialist certifications — a full professional badge portfolio, not just a course completion.</>}
        />

        <div className="grid sm:grid-cols-3 gap-4">
          <Stat label="Core modules completed" value={`${coreDone} / ${coreTotal}`} testid="stat-core" />
          <Stat label="Specialist tracks" value={specialists.length} testid="stat-specialist" />
          <Stat label="Certifications available" value={certs.length} testid="stat-certs" />
        </div>

        <BadgeRail certs={certs} passedSlugs={passedSlugs} primaryEarned={primaryEarned} />

        {/* Primary certification — Certified CPG Founder™ */}
        {primary && (
          <section className="mt-10">
            <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold">Primary certification</p>
            <div className="mt-3 grid lg:grid-cols-[1.35fr_1fr] gap-6 items-stretch">
              {/* Real certificate preview */}
              <div className="relative rounded-2xl overflow-hidden bg-[#0a1a3b] border border-shell-border shadow-lg" data-testid="cert-preview">
                <img
                  src={CERTIFICATE_IMAGE}
                  alt="The CPG Brand Launch System — Certificate of Completion"
                  className={["w-full h-auto block", primaryEarned ? "" : "opacity-70"].join(" ")}
                />
                {!primaryEarned && (
                  <div className="absolute inset-0 flex items-end justify-center p-6 bg-gradient-to-t from-[#0a1a3b]/80 via-transparent">
                    <div className="rounded-full bg-black/70 backdrop-blur px-5 py-2 text-white text-xs uppercase tracking-widest inline-flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5" />
                      Unlocks at {coreDone}/{coreTotal} complete
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl p-7 grad-band text-white flex flex-col justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">Primary certification</p>
                  <h2 className="font-display text-3xl mt-1">{primary.name}</h2>
                  <p className="mt-3 text-white/90 text-sm">{primary.description}</p>
                </div>
                <div className="mt-6">
                  <div className="flex items-end justify-between">
                    <p className="text-[10px] uppercase tracking-widest text-white/80">Your progress</p>
                    <p className="font-display text-3xl">{coreDone}<span className="text-white/70 text-xl">/{coreTotal}</span></p>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden" data-testid="primary-progress-bar">
                    <div className="h-full bg-white transition-all" style={{ width: `${primaryPct}%` }} />
                  </div>
                  <div className="mt-5">
                    {primaryEarned ? (
                      issuedMap["founder"] ? (
                        <div className="grid gap-2">
                          <button onClick={() => claim("founder")} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary px-5 py-2.5 font-semibold text-sm hover:bg-white/90"
                            data-testid="claim-primary">
                            <Sparkles className="w-4 h-4" /> Download my certificate
                          </button>
                          <div className="bg-white/10 rounded-xl p-2">
                            <ShareRow certSlug="founder" certName={primary.name} issued={issuedMap["founder"]} />
                          </div>
                        </div>
                      ) : (
                        <Link to="/exam/final" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary px-5 py-2.5 font-semibold text-sm hover:bg-white/90"
                          data-testid="claim-primary">
                          <Sparkles className="w-4 h-4" /> Take final exam to earn credential
                        </Link>
                      )
                    ) : (
                      <p className="text-xs text-white/80">
                        Complete each core module (pass the quiz or mark complete), then take the final 50-question exam to earn the primary credential.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CPG Master upgrade — always visible once the eligibility payload
            lands so paid-plan founders can see the criteria checklist from
            day one. The card itself renders three internal states
            (locked / eligible / earned) so the Download CTA only appears
            once the founder actually earns the credential. */}
        {master && (
          <MasterUpgradeCard
            state={master}
            claiming={claimingMaster}
            onClaim={claimMaster}
            onDownload={() => ensureNameThen(() => openPdf(master.credential_id))}
          />
        )}

        {/* Core module progress checklist */}
        <section className="mt-12">
          <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold">Core curriculum</p>
          <h2 className="font-display text-2xl mt-2">Ten modules to your primary badge</h2>
          <div className="mt-5 grid md:grid-cols-2 gap-3">
            {coreModules.map((m) => {
              const passed = passedSlugs.has(m.slug);
              const complete = completedSlugs.has(m.slug);
              const state = passed ? "passed" : complete ? "complete" : "todo";
              return (
                <Link key={m.slug} to={`/modules/${m.slug}`} data-testid={`core-row-${m.slug}`}
                  className="surface p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform">
                  <div className={[
                    "w-9 h-9 rounded-full grid place-items-center shrink-0",
                    state === "passed" ? "bg-green-100 text-green-700" :
                    state === "complete" ? "bg-pink-100 text-pink-700" :
                                           "bg-muted text-muted-foreground"
                  ].join(" ")}>
                    {state === "passed" ? <ClipboardCheck className="w-4 h-4" />
                      : state === "complete" ? <CheckCircle2 className="w-4 h-4" />
                      : <Lock className="w-4 h-4 opacity-60" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold break-words">{m.name}</p>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-widest">
                      {state === "passed" ? "Quiz passed" : state === "complete" ? "Marked complete" : "Not started"}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Specialist certifications */}
        <section className="mt-12">
          <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold">Specialist certifications</p>
          <h2 className="font-display text-2xl mt-2">Six additional specialist badges</h2>
          <p className="text-sm text-muted-foreground">Each bonus module ends in an assessment. Pass it, earn the specialist credential.</p>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {specialists.map((c) => {
              const module = bySlug[c.bonus_slug];
              const locked = module?.locked;
              const earned = passedSlugs.has(c.bonus_slug) || !!issuedMap[c.slug];
              return (
                <div key={c.slug} className={["surface p-6 flex flex-col relative overflow-hidden",
                  locked ? "opacity-80" : "hover:-translate-y-0.5 transition-transform"].join(" ")}
                  data-testid={`cert-specialist-${c.slug}`}>
                  {earned && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-green-100 text-green-700 rounded px-2 py-0.5 font-semibold">Earned</span>}
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-shell-accent/25 grid place-items-center text-2xl">{c.emoji}</div>
                    {locked ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Award className="w-4 h-4 text-shell-accent" />}
                  </div>
                  <h3 className="font-display text-lg mt-4 leading-tight">{c.name}</h3>
                  <p className="text-xs text-muted-foreground mt-2 flex-1">{c.description}</p>
                  {module ? (
                    locked ? (
                      <Link to="/account/subscription" className="mt-5 text-xs font-semibold text-primary" data-testid={`cert-upgrade-${c.slug}`}>Upgrade to unlock →</Link>
                    ) : earned ? (
                      <div className="mt-5 flex flex-col gap-2">
                        <button onClick={() => claim(c.slug)} className="btn-amber text-xs py-1.5" data-testid={`cert-claim-${c.slug}`}>
                          Download credential PDF
                        </button>
                        <ShareRow certSlug={c.slug} certName={c.name} issued={issuedMap[c.slug]} />
                      </div>
                    ) : (
                      <Link to={`/modules/${module.slug}`} className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-shell-side" data-testid={`cert-open-${c.slug}`}>
                        Open {module.name} <ExternalLink className="w-3 h-3" />
                      </Link>
                    )
                  ) : (
                    <p className="mt-5 text-xs text-muted-foreground">Coming soon</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <CertificateNameModal
        open={showNameModal}
        defaultName={user?.name || ""}
        onClose={() => {
          setShowNameModal(false);
          setPendingDownload(null);
        }}
        onSaved={(name) => {
          // Lock the state locally so subsequent downloads skip the
          // modal without a round-trip to /display-name.
          setDisplayName(name);
          setNameLocked(true);
          setShowNameModal(false);
          // Fire the deferred download that put us in this modal in
          // the first place. If a user opened the modal without a
          // pending download (rare), we simply close.
          const run = pendingDownload?.run;
          setPendingDownload(null);
          try { run?.(); } catch (_) { /* ignore */ }
        }}
      />
    </AppShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <p className="font-display text-3xl">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * ShareRow — LinkedIn "Add to Profile" button (deep link) + copy-verify-URL.
 * Uses LinkedIn's public certification add flow. The credentialId is the
 * globally-verifiable ID that lives at /verify/{credential_id}.
 */
function ShareRow({ certSlug, certName, issued }) {
  const { isPageElementHidden } = useMenuVisibility();
  if (!issued) return null;
  const site = process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, "") || "https://cpg.institute";
  const verifyUrl = `${site}/verify/${issued.credential_id}`;
  const issuedAt = new Date(issued.issued_at || Date.now());
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: certName,
    organizationName: "CPG Institute",
    issueYear: String(issuedAt.getFullYear()),
    issueMonth: String(issuedAt.getMonth() + 1),
    certUrl: verifyUrl,
    certId: issued.credential_id,
  });
  const linkedInUrl = `https://www.linkedin.com/profile/add?${params.toString()}`;

  const copy = () => {
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Verify link copied");
  };

  // Admin toggle: hide the LinkedIn deep-link button while keeping the
  // Copy-verify-URL affordance. Registered in menu_directory.py under
  // `page-progress → progress-add-to-linkedin`.
  const linkedInHidden = isPageElementHidden("progress-add-to-linkedin");

  return (
    <div className="flex items-center gap-2" data-testid={`cert-share-${certSlug}`}>
      {!linkedInHidden && (
        <a href={linkedInUrl} target="_blank" rel="noreferrer"
          data-testid={`cert-linkedin-${certSlug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0A66C2] text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-[#0952a1] transition-colors">
          <Linkedin className="w-3 h-3" /> Add to LinkedIn
        </a>
      )}
      <button onClick={copy} title="Copy verify link" data-testid={`cert-copy-${certSlug}`}
        className={[
          "rounded-full border border-shell-border p-1.5 hover:border-primary hover:text-primary transition-colors",
          // When LinkedIn is hidden the copy button becomes the primary
          // (and only) action — stretch it so the row still fills the
          // width and doesn't look like a broken half-row.
          linkedInHidden ? "flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5" : "",
        ].join(" ")}>
        <Copy className="w-3 h-3" />
        {linkedInHidden && <span>Copy verify link</span>}
      </button>
    </div>
  );
}


// ─── CPG Master upgrade card (Phase 3) ────────────────────────────────
function MasterUpgradeCard({ state, claiming, onClaim, onDownload }) {
  const c = state.criteria || {};
  const earned = state.already_earned;
  const eligible = state.eligible;
  // Once the Master credential exists, re-hitting the primary PDF endpoint
  // is a safe idempotent download — no side effects, no re-issue. The
  // parent-supplied `onDownload` gate ensures the display-name modal
  // fires the first time so every PDF renders with the founder's
  // permanent name.
  const downloadMaster = () => {
    if (!state.credential_id) return;
    if (onDownload) return onDownload();
    // Legacy fallback if the parent didn't wire the gate.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/certifications/pdf/${state.credential_id}`;
    window.open(url, "_blank");
  };
  return (
    <section
      className="mt-10 rounded-2xl overflow-hidden border-2 border-primary/30 bg-white"
      data-testid="master-upgrade-card"
    >
      <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
        <div className="shrink-0 grid place-items-center w-16 h-16 rounded-2xl bg-primary/10 text-3xl">
          👑
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
            Upgrade your certificate
          </p>
          <h2 className="font-display text-2xl mt-1">CPG Master™</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-prose">
            The highest credential in the CPG Institute. Reserved for founders who complete
            every module, every worksheet, and submit at least one Launch Blueprint.
            Awarded once — proof of end-to-end mastery.
          </p>

          <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="master-criteria">
            <CriteriaChip label="Modules"   done={c.modules?.done}    total={c.modules?.total}    ok={c.modules?.ok} />
            <CriteriaChip label="Worksheets"done={c.worksheets?.done} total={c.worksheets?.total} ok={c.worksheets?.ok} />
            <CriteriaChip label="Blueprint" done={c.blueprint?.done}  total={c.blueprint?.total}  ok={c.blueprint?.ok} />
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            {earned ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-semibold" data-testid="master-earned">
                  <CheckCircle2 className="w-4 h-4" /> Earned — Credential {state.credential_id}
                </span>
                {/* Download stays enabled so founders can re-fetch the PDF
                    any time — matches the Founder certificate affordance. */}
                <button
                  onClick={downloadMaster}
                  data-testid="master-download"
                  className="btn-primary text-sm inline-flex items-center gap-1.5"
                >
                  <Award className="w-4 h-4" /> Download my certificate
                </button>
              </>
            ) : eligible ? (
              <button onClick={onClaim} disabled={claiming} data-testid="master-claim"
                className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                <Award className="w-4 h-4" /> {claiming ? "Issuing…" : "Claim CPG Master™"}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="master-locked">
                <Lock className="w-4 h-4" /> Finish every step above to unlock
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function CriteriaChip({ label, done = 0, total = 0, ok }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className={[
      "rounded-xl border px-3 py-2 text-xs",
      ok ? "border-emerald-300 bg-emerald-50/60" : "border-shell-border bg-white",
    ].join(" ")}>
      <div className="flex items-center justify-between">
        <span className="uppercase tracking-widest text-[10px] font-bold text-muted-foreground">{label}</span>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Lock className="w-3.5 h-3.5 text-muted-foreground/60" />}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-lg font-display font-semibold">{done}<span className="text-muted-foreground text-sm"> / {total}</span></span>
        <span className="text-[10px] text-muted-foreground">{pct}%</span>
      </div>
      <div className="mt-1 h-1 rounded-full bg-muted overflow-hidden">
        <div className={["h-full rounded-full", ok ? "bg-emerald-500" : "bg-primary"].join(" ")}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
